<script lang="ts">
  import { onDestroy, untrack } from "svelte"
  import AsyncSelectInput from "@kontextwerk/web-sdk/ui/forms/AsyncSelectInput.svelte"
  import { tooltip } from "@kontextwerk/web-sdk/core/overlay/tooltip"
  import { fetchProperties } from "../../../../../api/kehrblatt/property-registry/properties"
  import { formatPropertyAddress, normalizeId } from "../../_logic/property-unit-selection"
  import { normalizeCountryLabel } from "../../../../../_shared/_logic/countries"

  type PropertySelectItem = {
    value: string
    label: string
    headline: string
    subline: string
  }
  type Props = {
    organizationId: string
    seedProperties?: Property[]
    disabled?: boolean
    clearable?: boolean
    brightBackground?: boolean
    id?: string
    placeholder?: string
    labelVisibility?: "floating" | "hidden"
    presentation?: "single-line" | "two-line"
    onChange?: (property: Property | null) => void
    value?: string
    selectedProperty?: Property | null
  }
  let {
    organizationId,
    seedProperties = [],
    disabled = false,
    clearable = true,
    brightBackground = true,
    id = "work-order-property",
    placeholder = "Liegenschaft wählen",
    labelVisibility = "floating",
    presentation = "single-line",
    onChange,
    value = $bindable(""),
    selectedProperty = null,
  }: Props = $props()
  let loadedProperties = $state<Property[]>([])
  let searchText = $state("")
  let searchFailed = $state(false)
  let generation = 0
  let searchGeneration = 0
  let alive = true
  const pending = new Set<string>()
  const cached = $derived.by(() => {
    const byId = new Map<string, Property>()
    for (const property of [
      ...seedProperties,
      ...loadedProperties,
      ...(selectedProperty ? [selectedProperty] : []),
    ]) {
      if (property.organization_id && property.organization_id !== organizationId) continue

      byId.set(normalizeId(property.id), property)
    }

    return byId
  })
  const currentProperty = $derived(cached.get(normalizeId(value)) ?? null)
  const toItem = (property: Property): PropertySelectItem => {
    const units = (property.units ?? []).filter((unit) => !unit.archived_at)
    // Match the district classification: a building with residential units is an MFH.
    const houseType = units.some((unit) => unit.unit_type === "BUILDING")
      ? units.some((unit) => unit.unit_type === "RESIDENTIAL")
        ? "MFH: "
        : "EFH: "
      : ""

    return {
      value: normalizeId(property.id),
      label: formatPropertyAddress(property),
      headline:
        houseType +
        ([property.street, property.house_number].filter(Boolean).join(" ") ||
          "Unbekannte Adresse"),
      subline: [
        [property.zip, property.city].filter(Boolean).join(" "),
        normalizeCountryLabel(property.country),
      ]
        .filter(Boolean)
        .join(", "),
    }
  }
  const itemForValue = (itemValue: unknown) => {
    const property = cached.get(normalizeId(itemValue))

    return property ? toItem(property) : null
  }
  const mergeLoaded = (properties: Property[]) => {
    const byId = new Map(loadedProperties.map((property) => [property.id, property]))
    properties.forEach((property) => byId.set(property.id, property))
    loadedProperties = [...byId.values()]
  }
  const loadOptions = async (input: string): Promise<PropertySelectItem[]> => {
    searchText = input.trim()
    searchFailed = false
    const requestId = ++searchGeneration

    if (searchText.length < 2) return []

    const scope = generation
    try {
      const result = await fetchProperties({ organizationId, search: searchText, limit: 25 })

      if (!alive || scope !== generation || requestId !== searchGeneration) return []

      mergeLoaded(result)

      return result.map(toItem)
    } catch {
      if (alive && scope === generation && requestId === searchGeneration) searchFailed = true

      return []
    }
  }
  const ensureSelected = async (propertyId: string) => {
    if (pending.has(propertyId)) return

    const scope = generation
    pending.add(propertyId)
    try {
      const result = await fetchProperties({ organizationId, propertyIds: [propertyId], limit: 1 })

      if (alive && scope === generation) mergeLoaded(result)
    } catch {
      // The selected id remains explicit; a failed lookup never substitutes another property.
    } finally {
      if (scope === generation) pending.delete(propertyId)
    }
  }
  $effect(() => {
    organizationId
    generation += 1
    searchGeneration += 1
    loadedProperties = []
    pending.clear()
  })
  $effect(() => {
    const propertyId = normalizeId(value)

    if (propertyId && !currentProperty)
      untrack(() => {
        void ensureSelected(propertyId)
      })
  })
  onDestroy(() => {
    alive = false
    generation += 1
  })
  const emptyLabel = $derived(
    searchFailed
      ? "Suche fehlgeschlagen. Bitte erneut versuchen."
      : searchText.length < 2
        ? "Mindestens 2 Zeichen für Ergebnisse"
        : "Keine Ergebnisse",
  )
</script>

<label class="fieldShell" data-input-id={id} class:disabled class:bright={brightBackground}>
  {#if labelVisibility !== "hidden"}<span class="labelspan hasValue">{placeholder}</span>{/if}
  <AsyncSelectInput
    {id}
    value={normalizeId(value)}
    selectedOption={currentProperty ? toItem(currentProperty) : null}
    {placeholder}
    ariaLabel={placeholder}
    {disabled}
    {clearable}
    {brightBackground}
    {loadOptions}
    {emptyLabel}
    classList={presentation === "two-line" ? "property-two-line-select" : ""}
    selectionRenderer={selectedValue}
    itemRenderer={optionValue}
    onSelectionChange={(item) => {
      const selectedId = normalizeId(item?.value)

      if (selectedId === normalizeId(value)) return

      value = selectedId
      onChange?.(selectedId ? (cached.get(selectedId) ?? null) : null)
    }}
  />
</label>

{#snippet selectedValue({ selection })}
  {@const item = itemForValue(selection?.value)}
  {#if item}{@render addressValue(item)}{/if}
{/snippet}
{#snippet optionValue({ item: option })}
  {@const item = itemForValue(option.value)}
  {#if item}{@render addressValue(item)}{/if}
{/snippet}
{#snippet addressValue(item: PropertySelectItem)}
  <span
    class="property-value"
    class:two-line={presentation === "two-line"}
    use:tooltip={{ content: item.label, focus: true }}
  >
    {#if presentation === "two-line"}
      <span class="property-value__headline">{item.headline}</span>
      <span class="property-value__subline">{item.subline}</span>
    {:else}
      <span>{item.label}</span>
    {/if}
  </span>
{/snippet}

<style lang="less">
  .fieldShell {
    width: 100%;
    max-width: 100%;
    min-width: 0;
  }
  .property-value {
    display: block;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .two-line {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    line-height: 1.2;
    padding-block: 0.25rem;
  }
  .property-value__headline {
    font-size: 0.9rem;
    font-weight: 700;
  }
  .property-value__subline {
    font-size: 0.76rem;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .fieldShell :global(.property-two-line-select) {
    --height: 48px;
    --item-height: 48px;
    --item-line-height: 1.2;
  }
  .fieldShell :global(.select-input-shell) {
    width: 100%;
    min-width: 0;
  }
  .fieldShell:not(.bright) .property-value {
    color: var(--text-100);
  }
</style>
